import { KoffieDienst, UitgezetteDienst } from "@/entities/dossier-overzicht";
import { SectionName } from "@/entities/dossier-settings";
import { Evaluatie } from "@/entities/evaluatie";

type BadgeVariant = "yellow-subtle" | "gray-subtle" | "green-subtle";

type BadgeInfo = {
    variant: BadgeVariant;
    text: "Nog actief" | "Niet van toepassing" | "Afgerond" | "Niet ingevuld";
};

type OnderdeelStatus = "openstaand" | "afgerond" | "niet-van-toepassing";

export type OpenstaandOnderdeel = {
    type: "tvv" | "faciliteiten" | "landelijkeFaciliteiten" | "busEvacuatie" | "vos" | "evaluatie";
    url: string;
    label: string;
    status: OnderdeelStatus;
    badge: BadgeInfo;
    section: SectionName;
};

const BadgeVariantTextMap: Record<BadgeVariant, BadgeInfo["text"]> = {
    "gray-subtle": "Niet van toepassing",
    "yellow-subtle": "Nog actief",
    "green-subtle": "Afgerond",
};

function getBadgeVariantForStatus(status: OnderdeelStatus): BadgeVariant {
    switch (status) {
        case "afgerond":
            return "green-subtle";
        case "openstaand":
            return "yellow-subtle";
        default:
            return "gray-subtle";
    }
}

function getBadgeForStatus(status: OnderdeelStatus, openstaandText: BadgeInfo["text"] = "Nog actief"): BadgeInfo {
    const variant = getBadgeVariantForStatus(status);

    if (status === "openstaand") {
        return { variant, text: openstaandText };
    }

    return { variant, text: BadgeVariantTextMap[variant] };
}

export function getStatusForEvaluatie(evaluatie?: Pick<Evaluatie, "heeftKhrStatus" | "icDocLink">): OnderdeelStatus {
    if (!evaluatie) return "niet-van-toepassing";

    return !evaluatie.heeftKhrStatus ? "niet-van-toepassing" : evaluatie.icDocLink ? "afgerond" : "openstaand";
}

export function getStatusForDienst(dienst?: UitgezetteDienst): OnderdeelStatus {
    if (!dienst) return "niet-van-toepassing";

    switch (dienst.type) {
        case "tvv": {
            return dienst.variant === "Gestopt" ? "afgerond" : "openstaand";
        }
        case "koffie": {
            // calculate variant without taking into account landelijke faciliteiten
            const variant = UitgezetteDienst.getDienstVariant("koffie", dienst.countByStatus)?.variant;
            // that also means it could be defined because of landelijke faciliteiten, but undefined here
            if (!variant) return "niet-van-toepassing";
            return variant === "Gestopt" || variant === "GaatNietLeveren" ? "afgerond" : "openstaand";
        }
        case "busEvacuatie": {
            return dienst.variant === "Afgerond" || dienst.variant === "Geannuleerd" ? "afgerond" : "openstaand";
        }
        case "vos": {
            return dienst.variant === "Afgerond" ? "afgerond" : "openstaand";
        }
    }
}

export function getStatusForLandelijkFaciliteiten(dienst?: KoffieDienst): OnderdeelStatus {
    if (!dienst?.landelijkeFaciliteiten) return "niet-van-toepassing";

    switch (dienst.landelijkeFaciliteiten) {
        case "Gestopt":
        case "GaatNietLeveren":
            return "afgerond";
        case "Besteld":
        case "Actief":
        case "StopAangevraagd":
            return "openstaand";
    }
}

export function getOpenstaandeOnderdelen(
    uitgezetteDiensten: UitgezetteDienst[],
    evaluatie?: Pick<Evaluatie, "heeftKhrStatus" | "icDocLink"> | null
) {
    const tvvStatus = getStatusForDienst(uitgezetteDiensten.find((dienst) => dienst.type === "tvv"));
    const koffieStatus = getStatusForDienst(uitgezetteDiensten.find((dienst) => dienst.type === "koffie"));
    const landelijkeFaciliteitenStatus = getStatusForLandelijkFaciliteiten(uitgezetteDiensten.find((dienst) => dienst.type === "koffie"));
    const busEvacuatieStatus = getStatusForDienst(uitgezetteDiensten.find((dienst) => dienst.type === "busEvacuatie"));
    const bemensingStatus = getStatusForDienst(uitgezetteDiensten.find((dienst) => dienst.type === "vos"));
    const evaluatieStatus = getStatusForEvaluatie(evaluatie ?? undefined);

    const openstaandeOnderdelen: OpenstaandOnderdeel[] = [
        { type: "tvv", label: "Businzet", url: "tvv", status: tvvStatus, badge: getBadgeForStatus(tvvStatus), section: "Tvv" },
        {
            type: "faciliteiten",
            label: "Faciliteiten",
            url: "faciliteiten",
            status: koffieStatus,
            badge: getBadgeForStatus(koffieStatus),
            section: "Faciliteiten",
        },
        {
            type: "landelijkeFaciliteiten",
            label: "Landelijke faciliteiten",
            url: "landelijke-faciliteiten",
            status: landelijkeFaciliteitenStatus,
            badge: getBadgeForStatus(landelijkeFaciliteitenStatus),
            section: "FaciliteitenLandelijk",
        },
        {
            type: "busEvacuatie",
            label: "Evacuatiebus",
            url: "betrokken-treinen",
            status: busEvacuatieStatus,
            badge: getBadgeForStatus(busEvacuatieStatus),
            section: "BetrokkenTreinen",
        },
        {
            type: "vos",
            label: "Vrijwilligers op station",
            url: "medewerkers-op-station",
            status: bemensingStatus,
            badge: getBadgeForStatus(bemensingStatus),
            section: "MedewerkersOpStation",
        },
        {
            type: "evaluatie",
            label: "Evaluatie",
            url: "evaluatie",
            status: evaluatieStatus,
            badge: getBadgeForStatus(evaluatieStatus),
            section: "Evaluatie",
        },
    ];

    return openstaandeOnderdelen;
}
